// src/pages/DashboardPage.jsx
// Phiên bản bước 3: thêm NotificationBell + FCM setup

import { useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import TaskCard from "../components/TaskCard";
import TaskModal from "../components/TaskModal";
import InviteModal from "../components/InviteModal"; 
import PendingInviteBanner from "../components/PendingInviteBanner";
import NotificationBell from "../components/NotificationBell";
import { useAuth } from "../contexts/AuthContext";
import { useTasks, useWorkspaces, useMembers } from "../hooks/useFirestore";
import { useFCM } from "../hooks/useFCM";
import { useInvites } from "../hooks/useInvites";
import toast from "react-hot-toast";
import "../styles/dashboard.css";

const STATUS_COLUMNS = [
  { key: "todo",        label: "Cần làm",    color: "#6b7280" },
  { key: "in_progress", label: "Đang làm",   color: "#f59e0b" },
  { key: "in_review",   label: "Xem xét",    color: "#6366f1" },
  { key: "done",        label: "Hoàn thành", color: "#10b981" },
];

export default function DashboardPage() {
  // Khởi động FCM (xin quyền push notification)
  useFCM();

  const { user } = useAuth();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState("board");
  


  const { workspaces } = useWorkspaces();
  const { tasks, loading, createTask, updateTask, deleteTask } = useTasks(activeWorkspaceId);
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);
  const members = useMembers(activeWorkspace?.members || []);
  const { sendInvite } = useInvites();
  // 🌟 THÊM DUY NHẤT DÒNG NÀY NGAY TẠI ĐÂY:
  const isOwner = activeWorkspace?.ownerId === user?.uid || activeWorkspace?.createdBy === user?.uid;	

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterPriority !== "all" && t.priority !== filterPriority) return false;
      if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [tasks, filterStatus, filterPriority, searchQuery]);

  const stats = useMemo(() => ({
    total: tasks.length,
    done: tasks.filter((t) => t.status === "done").length,
    inProgress: tasks.filter((t) => t.status === "in_progress").length,
    overdue: tasks.filter((t) => {
      const d = t.deadline?.toDate?.();
      return d && d < new Date() && t.status !== "done";
    }).length,
  }), [tasks]);

  const handleSaveTask = async (formData) => {
    try {
      if (editingTask?.id) {
        await updateTask(editingTask.id, formData);
        toast.success("Đã cập nhật task!");
      } else {
        await createTask(formData);
        toast.success("Đã tạo task mới!");
      }
      setShowModal(false);
      setEditingTask(null);
    } catch {
      toast.error("Có lỗi xảy ra, thử lại nhé.");
    }
  };

  const handleEditTask = (task) => { setEditingTask(task); setShowModal(true); };
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Xóa task này?")) return;
    try { await deleteTask(taskId); toast.success("Đã xóa."); }
    catch { toast.error("Xóa thất bại."); }
  };
  const handleStatusChange = async (taskId, newStatus) => {
    await updateTask(taskId, { status: newStatus });
  };
  const openNewTask = () => { setEditingTask(null); setShowModal(true); };

  return (
    <div className="dashboard">
      <Sidebar activeWorkspace={activeWorkspaceId} onSelectWorkspace={setActiveWorkspaceId} />

      <main className="dashboard-main">
	{/* CHÈN BANNER LỜI MỜI VÀO ĐÂY */}
        <PendingInviteBanner onAccepted={(workspaceId) => setActiveWorkspaceId(workspaceId)} />
        {!activeWorkspaceId ? (
          <EmptyState />
        ) : (
          <>
            {/* Header */}
            <header className="dash-header">
              <div className="dash-header-left">
                <h1 className="dash-title">{activeWorkspace?.name}</h1>
                {activeWorkspace?.description && (
                  <p className="dash-desc">{activeWorkspace.description}</p>
                )}
              </div>
              <div className="dash-header-actions">
                {/* 🔔 Chuông thông báo */}
                <NotificationBell />

		{/* THÊM NÚT MỜI THÀNH VIÊN Ở ĐÂY */}
    		{isOwner && (
		<button className="btn-secondary" onClick={() => setShowInviteModal(true)} style={{ marginRight: "8px" }}>
   			<svg width="14" height="14" viewBox="0 0 24 24" fill="none"
     			stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginRight: "4px" }}>
       			<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        		<circle cx="8.5" cy="7" r="4" />
        		<line x1="20" y1="8" x2="20" y2="14" />
        		<line x1="17" y1="11" x2="23" y2="11" />
      			</svg>
      			Thành viên
    		</button>
		)}

                <button className="btn-create" onClick={openNewTask}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  Tạo task
                </button>
              </div>
            </header>

            {/* Stats */}
            <div className="stats-row">
              <StatCard label="Tổng task"    value={stats.total}      color="#6366f1"/>
              <StatCard label="Hoàn thành"   value={stats.done}       color="#10b981"/>
              <StatCard label="Đang làm"     value={stats.inProgress} color="#f59e0b"/>
              <StatCard label="Trễ hạn"      value={stats.overdue}    color="#ef4444"/>
            </div>

            {/* Toolbar */}
            <div className="toolbar">
              <div className="toolbar-left">
                <div className="search-box">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Tìm task..." className="search-input"/>
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
                  <option value="all">Tất cả trạng thái</option>
                  <option value="todo">Cần làm</option>
                  <option value="in_progress">Đang làm</option>
                  <option value="in_review">Xem xét</option>
                  <option value="done">Hoàn thành</option>
                </select>
                <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="filter-select">
                  <option value="all">Tất cả ưu tiên</option>
                  <option value="high">Cao</option>
                  <option value="medium">Trung bình</option>
                  <option value="low">Thấp</option>
                </select>
              </div>
              <div className="view-toggle">
                <button className={`view-btn ${view === "board" ? "active" : ""}`} onClick={() => setView("board")} title="Kanban">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="18" rx="1"/>
                    <rect x="14" y="3" width="7" height="10" rx="1"/>
                    <rect x="14" y="17" width="7" height="4" rx="1"/>
                  </svg>
                </button>
                <button className={`view-btn ${view === "list" ? "active" : ""}`} onClick={() => setView("list")} title="Danh sách">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div className="tasks-loading">
                {[1,2,3].map(i => <div key={i} className="task-skeleton"/>)}
              </div>
            ) : view === "board" ? (
              <BoardView tasks={filteredTasks} members={members}
                onEdit={handleEditTask} onDelete={handleDeleteTask}
                onStatusChange={handleStatusChange} onNewTask={openNewTask}/>
            ) : (
              <ListView tasks={filteredTasks} members={members}
                onEdit={handleEditTask} onDelete={handleDeleteTask}
                onStatusChange={handleStatusChange}/>
            )}
          </>
        )}
      </main>
      {/* Cập nhật lại đoạn này ở cuối file */}
      {showInviteModal && (
        <InviteModal 
          workspace={{ ...activeWorkspace, sendInvite }} // Chỉnh sửa dòng này 👈
          members={members} 
          onClose={() => setShowInviteModal(false)} 
        />
      )}
	
      {showModal && (
        <TaskModal task={editingTask} members={members}
          onSave={handleSaveTask}
          onClose={() => { setShowModal(false); setEditingTask(null); }}/>
      )}
    </div>
  );
}

function BoardView({ tasks, members, onEdit, onDelete, onStatusChange }) {
  return (
    <div className="board-view">
      {STATUS_COLUMNS.map((col) => {
        const colTasks = tasks.filter((t) => t.status === col.key);
        return (
          <div key={col.key} className="board-col">
            <div className="board-col-header">
              <span className="col-dot" style={{ background: col.color }}/>
              <span className="col-label">{col.label}</span>
              <span className="col-count">{colTasks.length}</span>
            </div>
            <div className="board-col-body">
              {colTasks.length === 0 ? (
                <div className="col-empty">Trống</div>
              ) : colTasks.map((task) => (
                <TaskCard key={task.id} task={task} members={members}
                  onEdit={onEdit} onDelete={onDelete} onStatusChange={onStatusChange}/>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListView({ tasks, members, onEdit, onDelete, onStatusChange }) {
  if (!tasks.length) return <div className="list-empty">Không có task nào.</div>;
  return (
    <div className="list-view">
      {tasks.map((task) => (
        <TaskCard key={task.id} task={task} members={members}
          onEdit={onEdit} onDelete={onDelete} onStatusChange={onStatusChange}/>
      ))}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card">
      <span className="stat-value" style={{ color }}>{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
          <rect x="9" y="3" width="6" height="4" rx="2"/>
          <line x1="9" y1="12" x2="15" y2="12"/>
          <line x1="9" y1="16" x2="13" y2="16"/>
        </svg>
      </div>
      <h2>Chọn hoặc tạo workspace</h2>
      <p>Tạo một workspace mới ở thanh bên trái để bắt đầu quản lý công việc.</p>
    </div>
  );
}
